import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '../components/PageHeader.js';
import { useTenantContext } from '../api/tenant-context.js';
import { getPlatformStatus } from '../api/platform-status.js';
import { getMetrics, type MetricSeries } from '../api/metrics.js';
import {
  downloadReportCsv,
  getReportCatalog,
  runReportQuery,
  type ReportQueryParams,
  type ReportQueryResult,
} from '../api/reporting.js';
import { ApiError } from '../api/apiClient.js';

export function Reportes(): JSX.Element {
  const { tenant } = useTenantContext();
  const tenantId = tenant?.id ?? '';

  const statusQ = useQuery({
    queryKey: ['platform-status', tenantId],
    queryFn: () => getPlatformStatus(tenantId),
    enabled: !!tenantId,
  });

  const metricsQ = useQuery({
    queryKey: ['metrics', tenantId],
    queryFn: () => getMetrics(tenantId),
    enabled: !!tenantId,
    refetchInterval: 60_000,
  });

  const m = statusQ.data?.metrics;
  const series = metricsQ.data?.series ?? [];
  const summary = metricsQ.data?.summary ?? {};

  return (
    <>
      <PageHeader
        title="Reportes"
        subtitle="Métricas operativas del workspace. CloudWatch en tiempo real + counters de configuración."
      />

      {(statusQ.isLoading || metricsQ.isLoading) && (
        <div className="panel text-ink-3 text-sm">Cargando…</div>
      )}
      {statusQ.error && <div className="panel text-err text-sm">{(statusQ.error as Error).message}</div>}

      {/* ── Live metrics CloudWatch ───────────────────────────────────── */}
      {series.length > 0 && (
        <section className="mb-5">
          <SectionTitle>Métricas en tiempo real · últimas 24h</SectionTitle>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <Kpi label="Invocations" value={fmt(summary['invocations']?.total)} sub="total 24h" />
            <Kpi
              label="Errors"
              value={fmt(summary['errors']?.total)}
              sub="total 24h"
              tone={(summary['errors']?.total ?? 0) > 0 ? 'err' : 'ok'}
            />
            <Kpi
              label="Throttles"
              value={fmt(summary['throttles']?.total)}
              sub="total 24h"
              tone={(summary['throttles']?.total ?? 0) > 0 ? 'warn' : 'ok'}
            />
            <Kpi
              label="Duration p95"
              value={`${Math.round(summary['durationP95']?.avg ?? 0)} ms`}
              sub="promedio 24h"
            />
          </div>

          <div className="grid grid-cols-4 gap-3 mb-3">
            <Kpi label="API requests" value={fmt(summary['tenantApiRequests']?.total)} sub="tenant-mgmt 24h" tone="ok" />
            <Kpi
              label="API errors"
              value={fmt(summary['tenantApiErrors']?.total)}
              sub="5xx 24h"
              tone={(summary['tenantApiErrors']?.total ?? 0) > 0 ? 'err' : 'ok'}
            />
            <Kpi label="API latencia avg" value={`${Math.round(summary['tenantApiDurationAvg']?.avg ?? 0)} ms`} sub="avg 24h" />
            <Kpi label="Último req/h API" value={fmt(summary['tenantApiRequests']?.last)} sub="último bucket" />
          </div>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <Kpi label="Mensajes procesados" value={fmt(summary['tenantMessagesProcessed']?.total)} sub="flow-engine 24h" />
            <Kpi
              label="Mensajes con error"
              value={fmt(summary['tenantMessageErrors']?.total)}
              sub="flow-engine 24h"
              tone={(summary['tenantMessageErrors']?.total ?? 0) > 0 ? 'err' : 'ok'}
            />
            <Kpi label="Outbound sends" value={fmt(summary['tenantOutboundSends']?.total)} sub="connectors-social 24h" />
            <Kpi label="Handoffs CC" value={fmt(summary['tenantHandoffs']?.total)} sub="connectors-cc 24h" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <ChartPanel title="Requests del tenant por hora">
              <SeriesAreaChart series={findSeries(series, 'tenantApiRequests')} color="#FF2E3A" />
            </ChartPanel>
            <ChartPanel title="Latencia tenant (ms avg)">
              <SeriesAreaChart series={findSeries(series, 'tenantApiDurationAvg')} color="#3b82f6" />
            </ChartPanel>
            <ChartPanel title="Invocations stack (todas las Lambdas)">
              <SeriesAreaChart series={findSeries(series, 'invocations')} color="#FF2E3A" />
            </ChartPanel>
            <ChartPanel title="Errors por hora">
              <SeriesBarChart series={findSeries(series, 'errors')} color="#ef4444" />
            </ChartPanel>
            <ChartPanel title="Duration p95 (ms)">
              <SeriesAreaChart series={findSeries(series, 'durationP95')} color="#22c55e" />
            </ChartPanel>
            <ChartPanel title="Throttles por hora">
              <SeriesBarChart series={findSeries(series, 'throttles')} color="#eab308" />
            </ChartPanel>
          </div>
          <p className="mt-2 text-[11px] text-ink-4 italic">
            {metricsQ.data?._note}
          </p>
        </section>
      )}

      {/* ── Counters de configuración (platform-status) ─────────────────── */}
      {m && (
        <div className="space-y-5">
          <section>
            <SectionTitle>Recursos configurados</SectionTitle>
            <div className="grid grid-cols-4 gap-3">
              <Kpi label="Channels totales" value={String(m.channelsTotal)} />
              <Kpi label="Channels activos" value={String(m.channelsActive)} tone="ok" />
              <Kpi label="Connectors totales" value={String(m.connectorsTotal)} />
              <Kpi label="Connectors healthy" value={String(m.connectorsHealthy)} tone="ok" />
            </div>
          </section>

          <section>
            <SectionTitle>Campañas Inbound</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <Kpi label="Total" value={String(m.inboundCampaignsTotal)} />
              <Kpi label="Activas" value={String(m.inboundCampaignsActive)} tone="ok" />
            </div>
          </section>

          <section>
            <SectionTitle>Campañas Outbound</SectionTitle>
            <div className="grid grid-cols-5 gap-3">
              <Kpi label="Total" value={String(m.outboundCampaignsTotal)} />
              {(['draft', 'scheduled', 'running', 'completed', 'archived'] as const).map((s) => (
                <Kpi
                  key={s}
                  label={s}
                  value={String(m.outboundCampaignsByStatus[s] ?? 0)}
                  tone={s === 'completed' ? 'ok' : s === 'running' ? 'warn' : undefined}
                />
              ))}
            </div>
          </section>
        </div>
      )}

      <section className="mt-5">
        <SectionTitle>Reportes operacionales · query + export CSV</SectionTitle>
        <ReportsQueryPanel />
      </section>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// #158 — Reportes operacionales (catalog + query + export CSV).
// ──────────────────────────────────────────────────────────────────────────

function ReportsQueryPanel(): JSX.Element {
  const catalogQ = useQuery({
    queryKey: ['report-catalog'],
    queryFn: () => getReportCatalog(),
  });
  const items = catalogQ.data?.items ?? [];

  // Defaults sensatos: últimas 24h, limit 100.
  const today = new Date();
  const yesterday = new Date(today.getTime() - 24 * 3600 * 1000);
  const [reportId, setReportId] = useState('');
  const [from, setFrom] = useState(toIsoDate(yesterday));
  const [to, setTo] = useState(toIsoDate(today));
  const [limit, setLimit] = useState(100);
  const [result, setResult] = useState<ReportQueryResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (!reportId && items.length > 0) {
    // Pre-seleccionar el primero al cargar el catalog.
    setReportId(items[0]!.id);
  }

  const buildParams = (): ReportQueryParams => ({
    id: reportId,
    from: from ? `${from}T00:00:00Z` : undefined,
    to: to ? `${to}T23:59:59Z` : undefined,
    limit,
  });

  const runMut = useMutation({
    mutationFn: () => runReportQuery(buildParams()),
    onSuccess: (r) => { setResult(r); setErr(null); },
    onError: (e: unknown) => {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
      setResult(null);
    },
  });

  const exportMut = useMutation({
    mutationFn: () => downloadReportCsv(buildParams()),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setErr(null);
    },
    onError: (e: unknown) => setErr(e instanceof ApiError ? e.message : (e as Error).message),
  });

  const selectedDef = items.find((r) => r.id === reportId);

  return (
    <div className="space-y-3">
      <div className="panel">
        {catalogQ.isLoading && <p className="text-ink-3 text-sm">Cargando catálogo de reportes…</p>}
        {catalogQ.error && <p className="text-err text-sm">{(catalogQ.error as Error).message}</p>}
        {items.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
              <label className="block text-[13px]">
                <span className="mb-1 block text-ink-3">Reporte</span>
                <select
                  value={reportId}
                  onChange={(e) => setReportId(e.target.value)}
                  className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                >
                  {items.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </label>
              <label className="block text-[13px]">
                <span className="mb-1 block text-ink-3">Desde</span>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                />
              </label>
              <label className="block text-[13px]">
                <span className="mb-1 block text-ink-3">Hasta</span>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                />
              </label>
              <label className="block text-[13px]">
                <span className="mb-1 block text-ink-3">Límite (filas)</span>
                <input
                  type="number"
                  min={1}
                  max={10000}
                  value={limit}
                  onChange={(e) => setLimit(Math.max(1, Math.min(10000, Number(e.target.value) || 100)))}
                  className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                />
              </label>
            </div>
            {selectedDef && (
              <p className="text-[12px] text-ink-3 mb-3">
                {selectedDef.description} · Campos: <span className="mono">{selectedDef.fields.join(', ')}</span>
              </p>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => runMut.mutate()}
                disabled={!reportId || runMut.isPending}
                className="btn-primary"
              >
                {runMut.isPending ? 'Consultando…' : 'Ejecutar reporte'}
              </button>
              <button
                type="button"
                onClick={() => exportMut.mutate()}
                disabled={!reportId || exportMut.isPending}
                className="btn-secondary"
              >
                {exportMut.isPending ? 'Generando CSV…' : 'Exportar CSV'}
              </button>
            </div>
            {err && (
              <div className="mt-3 rounded border border-red-200 bg-red-50 p-2 text-[12px] text-red-700">
                {err}
              </div>
            )}
          </>
        )}
      </div>

      {result && <ReportResultTable result={result} />}
    </div>
  );
}

function ReportResultTable({ result }: { result: ReportQueryResult }): JSX.Element {
  const { rows, report } = result;
  if (rows.length === 0) {
    return (
      <div className="panel text-ink-3 text-sm">
        Sin filas para los filtros seleccionados.
      </div>
    );
  }
  const fields = report.fields.length > 0
    ? report.fields
    : Object.keys(rows[0] ?? {});
  return (
    <div className="panel" style={{ padding: 0 }}>
      <div className="px-4 py-2 border-b border-[var(--border)] flex items-center justify-between">
        <div className="text-[13px] font-semibold">{report.name}</div>
        <div className="text-[11px] text-ink-3">{rows.length} fila{rows.length === 1 ? '' : 's'}</div>
      </div>
      <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 bg-slate-50 text-left text-[11px] uppercase text-ink-3">
            <tr>
              {fields.map((f) => (
                <th key={f} className="px-3 py-2 whitespace-nowrap">{f}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row, i) => (
              <tr key={i}>
                {fields.map((f) => (
                  <td key={f} className="px-3 py-2 mono whitespace-nowrap">{fmtCell(row[f])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function toIsoDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fmtCell(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function findSeries(all: MetricSeries[], id: string): MetricSeries | undefined {
  return all.find((s) => s.id === id);
}

function fmt(v: number | undefined): string {
  if (v === undefined) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return String(Math.round(v));
}

function SectionTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-ink-4">
      {children}
    </h3>
  );
}

function Kpi({
  label,
  value,
  tone,
  sub,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn' | 'err';
  sub?: string;
}): JSX.Element {
  const color =
    tone === 'ok' ? 'text-ok' : tone === 'err' ? 'text-err' : tone === 'warn' ? 'text-warn' : 'text-ink-1';
  return (
    <div className="panel" style={{ padding: '14px 18px' }}>
      <div className="text-[11px] uppercase tracking-wide text-ink-4">{label}</div>
      <div className={`mt-1 text-[22px] font-semibold ${color}`}>{value}</div>
      {sub && <div className="mt-0.5 text-[10.5px] text-ink-4">{sub}</div>}
    </div>
  );
}

function ChartPanel({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  // Recharts ResponsiveContainer requiere parent con dimensiones fijas o
  // se queja con width/height=-1 al primer paint. Truco: position relative
  // + absolute child con inset:0 garantiza que ResponsiveContainer mida
  // siempre el panel parent (no el children intermedio).
  return (
    <div className="panel" style={{ padding: '14px 16px' }}>
      <div className="text-[12px] text-ink-3 mb-2">{title}</div>
      <div style={{ position: 'relative', height: 180, width: '100%' }}>
        <div style={{ position: 'absolute', inset: 0 }}>{children}</div>
      </div>
    </div>
  );
}

function SeriesAreaChart({
  series,
  color,
}: {
  series: MetricSeries | undefined;
  color: string;
}): JSX.Element {
  const data = useMemo(() => seriesToData(series), [series]);
  if (data.length === 0) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <defs>
          <linearGradient id={`grad-${color}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.4} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="t" tick={{ fontSize: 10, fill: '#5c5c63' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 10, fill: '#5c5c63' }} axisLine={false} tickLine={false} />
        <CartesianGrid strokeDasharray="3 3" stroke="#25252b" vertical={false} />
        <Tooltip contentStyle={{ background: '#1c1c21', border: '1px solid #34343c', fontSize: 12 }} />
        <Area type="monotone" dataKey="v" stroke={color} fill={`url(#grad-${color})`} strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function SeriesBarChart({
  series,
  color,
}: {
  series: MetricSeries | undefined;
  color: string;
}): JSX.Element {
  const data = useMemo(() => seriesToData(series), [series]);
  if (data.length === 0) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <XAxis dataKey="t" tick={{ fontSize: 10, fill: '#5c5c63' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 10, fill: '#5c5c63' }} axisLine={false} tickLine={false} />
        <CartesianGrid strokeDasharray="3 3" stroke="#25252b" vertical={false} />
        <Tooltip contentStyle={{ background: '#1c1c21', border: '1px solid #34343c', fontSize: 12 }} />
        <Bar dataKey="v" fill={color} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function seriesToData(s: MetricSeries | undefined): Array<{ t: string; v: number }> {
  if (!s) return [];
  return s.timestamps.map((ts, i) => ({
    t: new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    v: s.values[i] ?? 0,
  }));
}

function NoData(): JSX.Element {
  return (
    <div className="h-full flex items-center justify-center text-ink-4 text-[11px]">
      Sin datos en este rango.
    </div>
  );
}
